/**
 * Shared theme tokens and status/severity class maps.
 * Effective colors: `src/styles/brand.css` (`--mcq-*` aliases on :root / .dark).
 */

export const DEFAULT_ACCENT_COLOR = '#075beb';

export const THEME_TOKENS = {
  light: {
    ink: '#08152f',
    muted: '#475569',
    accent: '#075beb',
    accentStrong: '#064bc4',
    bg: '#f3f6fe',
    bgStart: '#f8faff',
    bgEnd: '#edf2fc',
    surface: '#ffffff',
    softSurface: '#f8faff'
  },
  dark: {
    ink: '#e2e8f0',
    muted: '#b3c1d8',
    accent: '#7db6ff',
    accentStrong: '#a8d0ff',
    bg: '#0c1430',
    bgStart: '#0c1430',
    bgEnd: '#111e3c',
    surface: '#111e3c',
    softSurface: '#152241'
  }
} as const;

export const ASSESSMENT_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  in_review: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  reviewed: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
};

export const ASSESSMENT_STATUS_FALLBACK =
  'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';

/** Integrity evidence: low is neutral slate (not "all clear"). */
export const INTEGRITY_SEVERITY_STYLES: Record<string, string> = {
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  critical: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
};

/** System issue cards include a border. */
export const ISSUE_SEVERITY_STYLES: Record<string, string> = {
  low: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
  medium: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  high: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-800',
  critical: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800'
};

/**
 * Live supervision alerts: low means healthy / all-clear (emerald),
 * high+critical share the danger family.
 */
export const LIVE_ALERT_SEVERITY_STYLES: Record<string, string> = {
  low: 'bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800',
  medium: 'bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800',
  high: 'bg-red-100 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800',
  critical: 'bg-red-100 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800'
};

export const EMOTION_FACE_STYLES = {
  detected: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  missing: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
} as const;

export function assessmentStatusBadgeClass(status: string): string {
  return ASSESSMENT_STATUS_STYLES[status] || ASSESSMENT_STATUS_FALLBACK;
}

export function integritySeverityClass(severity: string): string {
  return INTEGRITY_SEVERITY_STYLES[severity] || INTEGRITY_SEVERITY_STYLES.low;
}

export function issueSeverityClass(severity: string): string {
  return ISSUE_SEVERITY_STYLES[severity] || ISSUE_SEVERITY_STYLES.low;
}

export function liveAlertSeverityClass(severity?: string): string {
  const normalized = String(severity || 'low').toLowerCase();
  if (normalized === 'critical' || normalized === 'high') {
    return LIVE_ALERT_SEVERITY_STYLES.critical;
  }
  if (normalized === 'medium') {
    return LIVE_ALERT_SEVERITY_STYLES.medium;
  }
  return LIVE_ALERT_SEVERITY_STYLES.low;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '').trim();
  const full = normalized.length === 3
    ? normalized.split('').map((ch) => ch + ch).join('')
    : normalized;
  const value = Number.parseInt(full, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}

function channelLuminance(channel: number): number {
  const srgb = channel / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

export function contrastRatio(foreground: string, background: string): number {
  const lighter = Math.max(relativeLuminance(foreground), relativeLuminance(background));
  const darker = Math.min(relativeLuminance(foreground), relativeLuminance(background));
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG 2.1 AA for normal body text. */
export const WCAG_AA_BODY = 4.5;
export const WCAG_AA_LARGE = 3;
